import React, { PropsWithChildren, useContext, useState } from 'react';
import SvgMenuUser from '../icons/menu/MenuUser.tsx';
import { CompanyModuleEnum, PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import PermissionError from '@components/ringil3/PermissionError/PermissionError.tsx';
import AppSidebar from '@app/layout/AppSidebar.tsx';
import useSidebarState from '@app/layout/useSidebarState.tsx';
import { MenuItemModel } from '@components/menu/types.ts';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { useLanguageMenuItem } from '@hooks/i18n/useLanguageMenuItem.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { useAuth0 } from '@auth0/auth0-react';
import { Note } from '@components/styles.tsx';
import MaintenanceBanner from '@components/appMessages/MaintenanceBanner.tsx';
import SvgContacts from '@components/ringil3/icons/Contacts.tsx';
import SvgChartBar from '@icons/chart-bar.svg?react';
import useLockDocumentScroll from '@hooks/useLockDocumentScroll/useLockDocumentScroll.tsx';
import clsx from 'clsx';
import useShellMetrics from '@hooks/useShellMetrics/useShellMetrics.tsx';
import { useImprovedLayout } from '@app/layout/improvedLayoutStore.ts';
import { CONTENT_SCROLL_ATTR } from '@hooks/useScrollBehindPanel/useScrollBehindPanel.tsx';

interface ComponentProps {
  title?: string;
  oneOfPermission?: PermissionTypesEnum[];
  layout?: 'calendar';
}

const AdminLayout: React.FC<PropsWithChildren<ComponentProps>> = ({ title, oneOfPermission, layout, children }) => {
  // The shell below is exactly one viewport tall and scrolls internally — the document must never scroll too.
  useLockDocumentScroll();
  const sidebar = useSidebarState();
  const { contentRef } = useShellMetrics();
  const improvedLayout = useImprovedLayout();
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const { logout } = useAuth0();
  const languageMenuItem = useLanguageMenuItem();

  const { loggedUser, hasModule, hasPermission, hasPermissionAndModule, isCustomerAdmin, isCompanyAdmin, isCarrierAdmin } = useContext(AuthenticatedUserContext);
  const [visible, setVisible] = useState(false);

  // Every admin page requires PERMISSION_ADMIN; a page may additionally require one of oneOfPermission.
  const requiredPermissions = [PermissionTypesEnum.PERMISSION_ADMIN, ...(oneOfPermission ?? [])];

  if (isNullOrUndefined(loggedUser)) return <>Loading...</>;
  const showMessagedFromOwners =
    loggedUser?.modules?.filter(t => t.inheritingModule === true).length > 0 || loggedUser?.modules?.some(t => t.companyModule === CompanyModuleEnum.CARRIER);

  const showNotificationAdmin =
    loggedUser?.modules?.filter(t => t.inheritingModule === false).length > 0 && !loggedUser?.modules?.some(t => t.companyModule === CompanyModuleEnum.CARRIER);

  if (!hasPermission([PermissionTypesEnum.PERMISSION_ADMIN]) || (oneOfPermission?.length > 0 && !hasPermission(oneOfPermission)))
    return (
      <>
        <PermissionError permission={requiredPermissions} />
      </>
    );

  const appMenu: MenuItemModel[] = [
    {
      id: 'companies',
      title: 'Companies',
      render: true,
      mode: 'open-inside',
      icon: <SvgContacts />,
      subMenu: [
        { title: 'Create company', render: true, url: '/admin/company' },
        { title: 'List companies', render: true, url: '/admin/companies' },
        { title: 'Ship companies', render: true, url: '/admin/ship-companies' },
      ],
    },
    {
      id: 'imports',
      title: 'Imports',
      render: true,
      mode: 'open-inside',
      icon: <SvgContacts />,
      subMenu: [
        { title: 'Import users', render: true, url: '/admin/import/users' },
        { title: 'Import company locations', render: true, url: '/admin/import/company-locations' },
        { title: 'Import company with users', render: true, url: '/admin/import/company-with-users' },
      ],
    },
    {
      id: 'security',
      title: 'Security',
      render: true,
      mode: 'open-inside',
      icon: <SvgContacts />,
      subMenu: [
        { title: 'Users', render: true, url: '/admin/users' },
        { title: 'Security roles', render: true, url: '/admin/security/roles' },
        { title: 'Security usage', render: true, url: '/admin/security/usage' },
      ],
    },
    {
      id: 'tools',
      title: 'Tools',
      render: true,
      mode: 'open-inside',
      icon: <SvgContacts />,
      subMenu: [
        { title: 'I18N', render: true, url: '/admin/i18n' },
        { title: 'HTTP requests', render: true, url: '/admin/requests' },
        { title: 'Email requests', render: true, url: '/admin/email-monitoring' },
        { title: 'Find requests', render: true, url: '/admin/find-email-request' },
        { title: 'Resolved email requests', render: true, url: '/admin/email-monitoring2' },
        { title: 'Find resolved emails', render: true, url: '/admin/find-resolved-emails' },
        { title: 'Email history', render: true, url: '/admin/email-history' },
        { title: 'Alert message', render: true, url: '/admin/tools/web-alert' },
        { title: 'Webhook tests', render: true, url: '/admin/tools/webhook-tests' },
        { title: 'Webhook deliveries', render: true, url: '/admin/tools/webhook-deliveries' },
        { title: 'Cargo templates', render: true, url: '/admin/tools/cargo-templates' },
        { title: 'Calendar card designer', render: true, url: '/admin/tools/calendar-card-designer' },
        { title: 'Console', render: true, url: '/admin/tools/console' },
        { title: 'Elastic', render: true, url: '/admin/tools/elastic' },
        { title: 'Async jobs', render: true, url: '/admin/jobs-monitoring' },
        { title: 'Audit log', render: true, url: '/admin/tools/audit-log' },
      ],
    },
    {
      id: 'reporting',
      title: 'Reporting',
      render: true,
      mode: 'open-inside',
      icon: <SvgChartBar />,
      subMenu: [{ title: 'Dashboard recalculation', render: true, url: '/admin/reporting/dashboard-recalculation' }],
    },
  ] as MenuItemModel[];

  const userMenu = [
    languageMenuItem,
    {
      title: (
        <div>
          <div data-testid="loginUserName">
            {loggedUser?.firstName} {loggedUser?.lastName}
          </div>
          <Note data-testid="loginUserCompanyName">{loggedUser?.company}</Note>
        </div>
      ),
      icon: <SvgMenuUser />,
      mode: 'open-outside-bottom',
      render: true,
      subMenu: [
        {
          title: tr('Layout.profile', 'Profile'),
          url: nav.createNavigationLink(nav.urlFunctions.createMyProfile()),
          render: true,
        },
        {
          title: tr('Layout.logout', 'Logout'),
          onClick: () => logout({ logoutParams: { returnTo: window.location.origin } }),
          render: true,
        },
      ],
    },
  ] as MenuItemModel[];

  if (isNullOrUndefined(loggedUser)) <></>;

  return (
    <>
      {title && <title>{title}</title>}
      <div className="flex flex-col h-screen overflow-hidden">
        <MaintenanceBanner />
        <div className={'flex-1 min-h-0 flex flex-row bg-base-tertiary min-w-[1280px] overflow-hidden'}>
        <AppSidebar
          state={sidebar}
          menu={appMenu}
          userMenu={userMenu}
          menuStorageKey={'AdminLayout.menuOpen1'}
          userMenuStorageKey={'AdminLayout.menuOpen2'}
          panelClassName={'bg-white'}
          enableMobile={false}
        />
        {layout === 'calendar' ? (
          <main className="px-4 pt-4">{children}</main>
        ) : (
          <main className={'relative flex-1 flex overflow-hidden w-full min-w-0'}>
            <div
              ref={contentRef}
              className={clsx('flex-1 min-w-0 ml-[var(--app-gutter,24px)] mr-[var(--app-gutter,24px)] overflow-y-scroll pl-0.5 pb-0.5 pr-0.5 gap-2 flex-col', {
                'max-w-[1550px]': improvedLayout,
              })}
              {...{ [CONTENT_SCROLL_ATTR]: true }}
            >
              {children}
            </div>
          </main>
        )}
        </div>
      </div>
    </>
  );
};

export default AdminLayout;
